import { of, zip } from 'rxjs'
import { debounceTime, filter, map, mergeMap } from 'rxjs/operators'
import { matchPath } from 'react-router-dom'
import { ofType, Epic } from 'redux-observable'
import { AnyAction } from 'redux'
import { omit } from 'lodash-es'
import { find, path, pathOr } from 'ramda'
import * as Sentry from '@sentry/react'
import { tag } from 'rxjs-spy/operators/tag'
import * as chatbotSelectors from './selectors'
import { getCurrentLeaseType, getInvoices, getRemovedInvoices, getUpdatedInvoiceIds } from './selectors'
import { chatbotEvents } from './state'
import { partyApiEvents } from '../../api/party'
import { portfolioApiEvents } from '../../api/portfolio'
import { ownerDetailsEvents } from './Dialogs/ownerDetailsSlice'
import { tenantDetailsEvents } from './Dialogs/tenantDetailsSlice'
import { hasDupsStrict, uniqObjectsStrict } from 'utils/uniq'
import { userApiSelectors } from 'modules/api/user'
import { uiEvents } from '..'
import { layoutEvents } from 'modules/Layout/state'
import { NAMESPACE as PORTFOLIOS_NAMESPACE } from 'modules/portfolios/constants'
import { ROUTES } from 'constants/routes'
import { $TSFixMe } from 'types/ts-migrate'
import { EpicDependencies, RootState } from 'src/state/types'
import { leaseTermEvents } from './Dialogs/leaseTermSlice'
import { leaseRentAndFeesEvents } from './Dialogs/leaseRentAndFeesSlice'
import { leaseSettingsEvents } from './Dialogs/leaseSettingsSlice'
import { leaseCommissionEvents } from './Dialogs/leaseCommissionSlice'
import { leaseInvoicesEvents } from './Dialogs/leaseInvoicesSlice'
import { leaseConfirmationEvents } from './Dialogs/leaseConfirmationSlice'

export const selectOwnerAfterCreated: Epic<AnyAction, AnyAction, RootState, EpicDependencies> = action$ =>
  action$.pipe(
    ofType(partyApiEvents.createParty_success),
    map(({ payload }) => Object.values(payload)[0]),
    filter((party: $TSFixMe) => party.tags.indexOf('Owner') !== -1),
    map(party => ownerDetailsEvents.partySaved(party)),
    tag('chatbot/epics/selectOwnerAfterCreated'),
  )

export const selectTenantAfterCreated: Epic<AnyAction, AnyAction, RootState, EpicDependencies> = action$ =>
  action$.pipe(
    ofType(partyApiEvents.createParty_success),
    map(({ payload }) => Object.values(payload)[0]),
    filter((party: $TSFixMe) => party.tags.indexOf('Tenant') !== -1),
    map(party => tenantDetailsEvents.partySaved(party)),
    tag('chatbot/epics/selectTenantAfterCreated'),
  )

export const saveLeaseTermsOnChanged: Epic<AnyAction, AnyAction, RootState, EpicDependencies> = (action$, state$) =>
  action$.pipe(
    ofType(leaseTermEvents.selectLeaseTerms),
    map(() => ({
      portfolioId: chatbotSelectors.getCurrentPortfolioId(state$.value),
      term: chatbotSelectors.getLeaseTermDialog(state$.value).term,
    })),
    filter(({ portfolioId, term }) => Boolean(portfolioId && term)),
    map(({ portfolioId, term }) =>
      portfolioApiEvents.amendTerms_request({
        body: {
          startDate: term.startDate,
          endDate: term.endDate,
          rolloverMonthToMonth: term.rolloverMonthToMonth,
        },
        params: { id: portfolioId },
      }),
    ),
    tag('chatbot/epics/saveLeaseTermsOnChanged'),
  )

export const saveLeaseRentAndFees: Epic<AnyAction, AnyAction, RootState, EpicDependencies> = (action$, state$) =>
  action$.pipe(
    ofType(leaseRentAndFeesEvents.rentAndFeesSaved),
    filter(() => chatbotSelectors.getCurrentPortfolioId(state$.value) !== null),
    map(() =>
      portfolioApiEvents.amendRentAndFees_request({
        body: {
          type: getCurrentLeaseType(state$.value),
          value: { terms: chatbotSelectors.getLeaseRentAndFeesDialog(state$.value) },
        },
        params: { id: chatbotSelectors.getCurrentPortfolioId(state$.value) },
      }),
    ),
    tag('chatbot/epics/saveLeaseRentAndFees'),
  )

export const saveLeaseSettings: Epic<AnyAction, AnyAction, RootState, EpicDependencies> = (action$, state$) =>
  action$.pipe(
    ofType(leaseSettingsEvents.settingsSaved),
    mergeMap(() => {
      const portfolioId = chatbotSelectors.getCurrentPortfolioId(state$.value)
      if (!portfolioId) return []
      const settings = chatbotSelectors.getLeaseSettingsDialog(state$.value)
      const defaultSegmentId = userApiSelectors.getCurrentAgencyId(state$.value)
      const segmentIds = pathOr([], ['segments'], settings).map(({ id }: any) => id)
      const params = { id: portfolioId }

      return [
        portfolioApiEvents.amendSettings_request({
          body: {
            ...settings,
            segments: [defaultSegmentId, ...segmentIds],
          },
          params,
        }),
      ]
    }),
    tag('chatbot/epics/saveLeaseSettings'),
  )

export const saveLeaseCommissionData: Epic<AnyAction, AnyAction, RootState, EpicDependencies> = (action$, state$) =>
  action$.pipe(
    ofType(leaseCommissionEvents.commissionSaved),
    filter(() => chatbotSelectors.getCurrentPortfolioId(state$.value) !== null),
    map(() =>
      portfolioApiEvents.amendCommission_request({
        body: chatbotSelectors.getLeaseCommissionDialog(state$.value),
        params: { id: chatbotSelectors.getCurrentPortfolioId(state$.value) },
      }),
    ),
    tag('chatbot/epics/saveLeaseCommissionData'),
  )

export const saveLeaseInvoiceTemplateData: Epic<AnyAction, AnyAction, RootState, EpicDependencies> = (
  action$,
  state$,
) =>
  action$.pipe(
    ofType(leaseInvoicesEvents.leaseInvoiceTemplatesSaved),
    mergeMap(() => {
      const id = chatbotSelectors.getCurrentPortfolioId(state$.value)

      if (!id) {
        return []
      }

      const invoices = getInvoices(state$.value)
      const newTemplates = invoices.filter((inv: any) => inv.id === undefined)
      const updatedTemplateIds = getUpdatedInvoiceIds(state$.value)
      const removedTemplateIds = getRemovedInvoices(state$.value)
      const updatedTemplates = updatedTemplateIds.map((id: any) => find((inv: any) => inv.id === id, invoices))
      const updatedTemplatesFiltered = updatedTemplates.filter((t: any) => t !== undefined)
      const omitUnsetAmount = (tmpl: any) => {
        if (!tmpl) {
          return tmpl
        }

        return !tmpl.netAmount ? omit(tmpl, ['netAmount']) : tmpl
      }

      const templateEvents = [
        ...newTemplates.map((t: any) =>
          portfolioApiEvents.addInvoiceTemplate_request({ body: omitUnsetAmount(t), params: { id } }),
        ),
        ...updatedTemplatesFiltered.map((t: any) =>
          portfolioApiEvents.updateInvoiceTemplate_request({
            body: omitUnsetAmount(t),
            params: { id, invoiceId: t.id },
          }),
        ),
        ...removedTemplateIds.map((invoiceId: any) =>
          portfolioApiEvents.removeInvoiceTemplate_request({ id, invoiceId: invoiceId }),
        ),
      ]

      // post to sentry to track occurances
      if (hasDupsStrict(templateEvents)) {
        const id = userApiSelectors.getUserId(state$.value)
        const email = userApiSelectors.getUserEmail(state$.value)
        const username = userApiSelectors.getUserFirstName(state$.value)
        Sentry.captureEvent({
          message: JSON.stringify(templateEvents),
          environment: process.env.NODE_ENV,
          user: { id: id ?? undefined, email, username },
        })
      }

      /**
       * Remove strictly duplicate events as a precaution
       * https://app.clickup.com/t/5wjtr3
       */
      return zip(...uniqObjectsStrict(templateEvents).map((a: any) => of(a)))
    }),
    mergeMap((actions: any[]) => actions as AnyAction[]),
    tag('chatbot/epics/saveLeaseInvoiceTemplateData'),
  )

export const fetchPortfolioOnSetRedirect: Epic<AnyAction, AnyAction, RootState, EpicDependencies> = (
  action$,
  state$,
  { windowLocation },
) =>
  action$.pipe(
    ofType(chatbotEvents.setCurrentPortfolio),
    map(p => p.payload.id),
    filter(p => p !== null && p !== false),
    debounceTime(1000),
    map(() => windowLocation().pathname),
    // Only redirect when the URL still contains the `new` placeholder that needs to be swapped
    // for the real portfolio id. Redirecting to an already-resolved URL is a no-op navigation
    // that re-triggers portfolio/party loading and drives a render loop on steps like Commission.
    filter(pathname => /\bnew\b/.test(pathname)),
    map(pathname => pathname.replace(/\bnew\b/, ':id')),
    map(chatbotEvents.portfolioRedirect),
    tag('chatbot/epics/fetchPortfolioOnSetRedirect'),
  )

// Step-advance redirects are draft-only — see `isCurrentPortfolioDraft`.
const isCurrentPortfolioDraft = chatbotSelectors.isCurrentPortfolioDraft

export const redirectToRentAndFees: Epic<AnyAction, AnyAction, RootState, EpicDependencies> = (action$, state$) =>
  action$.pipe(
    ofType(leaseTermEvents.selectLeaseTerms),
    filter(() => isCurrentPortfolioDraft(state$.value)),
    map(_ => chatbotEvents.portfolioRedirect('/leases/:id/edit/rent-and-fees')),
    tag('chatbot/epics/redirectToRentAndFees'),
  )

export const redirectToSettings: Epic<AnyAction, AnyAction, RootState, EpicDependencies> = (action$, state$) =>
  action$.pipe(
    ofType(leaseInvoicesEvents.leaseInvoiceTemplatesSaved),
    filter(() => isCurrentPortfolioDraft(state$.value)),
    map(_ => chatbotEvents.portfolioRedirect('/leases/:id/edit/settings')),
    tag('chatbot/epics/redirectToSettings'),
  )

export const redirectToCommission: Epic<AnyAction, AnyAction, RootState, EpicDependencies> = (action$, state$) =>
  action$.pipe(
    ofType(leaseRentAndFeesEvents.rentAndFeesSaved),
    filter(() => isCurrentPortfolioDraft(state$.value)),
    map(_ => chatbotEvents.portfolioRedirect('/leases/:id/edit/commission')),
    tag('chatbot/epics/redirectToCommission'),
  )

export const redirectToInvoicesMaybe: Epic<AnyAction, AnyAction, RootState, EpicDependencies> = (action$, state$) =>
  action$.pipe(
    ofType(leaseCommissionEvents.commissionSaved),
    filter(() => isCurrentPortfolioDraft(state$.value)),
    map(_ => chatbotEvents.portfolioRedirect('/leases/:id/edit/invoices')),
    tag('chatbot/epics/redirectToInvoicesMaybe'),
  )

export const redirectToActivatedLease: Epic<AnyAction, AnyAction, RootState, EpicDependencies> = (action$, state$) =>
  action$.pipe(
    ofType(portfolioApiEvents.approve_success),
    mergeMap(() =>
      of(
        layoutEvents.closeSidebar('global'),
        layoutEvents.openPrimaryPanel(PORTFOLIOS_NAMESPACE),
        chatbotEvents.portfolioRedirect('/leases/:id'),
      ),
    ),
    tag('chatbot/epics/redirectToActivatedLease'),
  )

export const requestLeaseApprovalSent: Epic<AnyAction, AnyAction, RootState, EpicDependencies> = (action$, state$) =>
  action$.pipe(
    ofType(leaseConfirmationEvents.approvalRequested),
    map(_ => chatbotEvents.portfolioRedirect('/leases/:id/edit/approval-sent')),
    tag('chatbot/epics/requestLeaseApprovalSent'),
  )

/**
 * @todo Temp code please remove later
 */
export const invoiceRedirectToActivate: Epic<AnyAction, AnyAction, RootState, EpicDependencies> = (action$, state$) =>
  action$.pipe(
    ofType(leaseSettingsEvents.settingsSaved),
    filter(() => isCurrentPortfolioDraft(state$.value)),
    map(_ => chatbotEvents.portfolioRedirect('/leases/:id/edit/activation')),
    tag('chatbot/epics/invoiceRedirectToActivate'),
  )

export const loadCommissionParties: Epic<AnyAction, AnyAction, RootState, EpicDependencies> = (action$, state$) =>
  action$.pipe(
    ofType(uiEvents.redirect),
    map(action => {
      const route: $TSFixMe = path(['meta', 'pushToRoute'], action)
      const isCommissionPage = matchPath(route, {
        path: `${ROUTES.portfolioEdit}/commission`,
        exact: true,
        strict: false,
      })
      return isCommissionPage ? partyApiEvents.quickSearch_request({ tags: 'Agent, Agency' }) : false
    }),
    filter(action => !!action),
    tag('chatbot/epics/loadCommissionParties'),
  )
